import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

/**
 * Widget CAPTCHA Cloudflare Turnstile + hook de challenge adaptatif.
 *
 * ⚠️ Fichier volontairement IDENTIQUE dans les 5 fronts web (client, admin, ops,
 * architect, travauxbtp). Le monorepo n'a pas de paquet UI web partagé — c'est
 * la même duplication assumée que `src/lib/sentry.ts`. Toute correction ici doit
 * être répercutée dans les autres copies.
 *
 * Deux usages :
 *  1. Widget permanent sur les inscriptions (`auth.register`, `registerPro`,
 *     `privateProjects.createAnonymous`) : un token est exigé à chaque appel.
 *     Rendu en `interaction-only` — rien n'apparaît à l'écran tant que
 *     Cloudflare n'exige pas d'interaction ; l'appelant récupère le token via
 *     `ref.getToken()` au moment de la soumission.
 *  2. Challenge adaptatif via `useCaptchaChallenge()` sur `auth.login`,
 *     `forgotPassword` et `sendPhoneOtp` : le serveur ne réclame un token
 *     qu'au-delà d'un seuil de comportement suspect, et le signale en renvoyant
 *     la sentinelle CAPTCHA_REQUIRED. Le widget n'apparaît qu'à ce moment.
 *
 * Graceful no-op sans `VITE_TURNSTILE_SITE_KEY` : rien n'est rendu et aucun
 * token n'est émis — le serveur bypasse alors la vérification en dev/test.
 */

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Vrai quand une site key est configurée — donc quand un token est exigible. */
export const isCaptchaConfigured = Boolean(SITE_KEY);

/**
 * Sentinelle renvoyée par le serveur (utils/captchaStepUp.ts) pour demander un
 * challenge. Dupliquée ici plutôt que partagée : seuls server, client et mobile
 * dépendent de @constrigo/shared-types, et l'y ajouter pour une chaîne
 * imposerait la dépendance à 3 apps de plus.
 */
export const CAPTCHA_REQUIRED = "CAPTCHA_REQUIRED";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

// Le script Cloudflare est global : une seule injection par page, quel que soit
// le nombre de widgets montés.
let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Ne pas mémoriser l'échec : un remontage doit pouvoir retenter
      // (coupure réseau passagère).
      scriptPromise = null;
      reject(new Error("Turnstile script failed to load"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export interface TurnstileHandle {
  /** Redemande un challenge — un token Turnstile est à USAGE UNIQUE. */
  reset: () => void;
  /**
   * Résout avec le token dès qu'il est disponible (immédiatement s'il l'est
   * déjà). Indispensable en `interaction-only` : le widget étant invisible, un
   * utilisateur rapide peut soumettre avant que Cloudflare ait répondu — sans
   * cette attente il verrait « validez le contrôle anti-robot » en pointant un
   * widget qui n'existe pas à l'écran.
   *
   * Résout `null` (jamais ne pend indéfiniment) si le widget a échoué, n'est pas
   * configuré, ou si le délai expire — l'appelant traite alors l'absence de
   * token comme avant.
   */
  getToken: (timeoutMs?: number) => Promise<string | null>;
}

interface TurnstileProps {
  /** Reçoit le token, ou `null` quand il expire / échoue / est réinitialisé. */
  onToken: (token: string | null) => void;
  className?: string;
  /**
   * `interaction-only` (défaut) : le widget reste INVISIBLE et se résout en
   * silence ; Cloudflare ne l'affiche que s'il exige une interaction humaine.
   * C'est le mode voulu sur les formulaires où le widget est monté en
   * permanence — l'utilisateur légitime ne voit jamais rien.
   *
   * `always` : widget toujours visible. Réservé au challenge adaptatif
   * (`useCaptchaChallenge`), où le serveur vient d'annoncer « validez le
   * contrôle anti-robot ci-dessous » : un widget invisible laisserait ce
   * message désigner le vide.
   */
  appearance?: "always" | "interaction-only";
}

export const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(function Turnstile(
  { onToken, className, appearance = "interaction-only" },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);

  // Miroirs en ref du token et de l'échec : `getToken` est figé par un
  // useImperativeHandle sans dépendances et ne verrait jamais l'état React.
  const tokenRef = useRef<string | null>(null);
  const failedRef = useRef(false);
  const waitersRef = useRef<Array<(token: string | null) => void>>([]);

  // `onToken` passe par une ref : le widget ne doit se monter qu'une fois, pas à
  // chaque rendu du parent qui recrée son callback.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  /** Publie le token vers le parent ET débloque les `getToken()` en attente. */
  const emitToken = useCallback((token: string | null) => {
    tokenRef.current = token;
    onTokenRef.current(token);
    // On ne réveille les attentes que sur un token utilisable, ou sur échec
    // définitif (emitFailure) : un `null` d'expiration ne doit pas faire
    // échouer une soumission alors qu'un nouveau challenge est en cours.
    if (token) waitersRef.current.splice(0).forEach((resolve) => resolve(token));
  }, []);

  /** Échec définitif : plus aucun token n'arrivera, on libère les attentes. */
  const emitFailure = useCallback(() => {
    failedRef.current = true;
    setFailed(true);
    tokenRef.current = null;
    onTokenRef.current(null);
    waitersRef.current.splice(0).forEach((resolve) => resolve(null));
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          tokenRef.current = null;
          onTokenRef.current(null);
        }
      },
      getToken: (timeoutMs = 15000) =>
        new Promise<string | null>((resolve) => {
          if (tokenRef.current) return resolve(tokenRef.current);
          // Sans site key aucun widget n'est monté : répondre tout de suite
          // plutôt que de faire patienter 15 s pour rien (dev/test).
          if (!SITE_KEY || failedRef.current) return resolve(null);

          const waiter = (token: string | null) => {
            clearTimeout(timer);
            resolve(token);
          };
          const timer = setTimeout(() => {
            waitersRef.current = waitersRef.current.filter((w) => w !== waiter);
            resolve(null);
          }, timeoutMs);
          waitersRef.current.push(waiter);
        }),
    }),
    []
  );

  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          // `auto` suivrait le thème de l'OS du visiteur alors qu'aucune de nos
          // apps ne bascule en sombre (tailwind `darkMode: ["class"]`, classe
          // jamais posée) : un macOS en sombre affichait un boîtier noir au
          // milieu d'une carte blanche.
          theme: "light",
          appearance,
          callback: (token: string) => emitToken(token),
          // Un token expire (~5 min) et ne vaut qu'une fois : on le purge côté
          // parent pour ne jamais soumettre un jeton périmé, que le serveur
          // rejetterait avec une erreur incompréhensible pour l'utilisateur.
          "expired-callback": () => emitToken(null),
          "error-callback": () => emitFailure(),
        });
      })
      .catch(() => {
        if (!cancelled) emitFailure();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [appearance, emitToken, emitFailure]);

  if (!SITE_KEY) return null;

  return (
    <div className={className}>
      <div ref={containerRef} />
      {failed && (
        <p className="mt-1 text-xs text-destructive">
          Le contrôle anti-robot n'a pas pu se charger. Vérifiez votre connexion, puis rechargez la page.
        </p>
      )}
    </div>
  );
});

/** Message affiché quand le serveur vient de réclamer un challenge. */
export const CAPTCHA_CHALLENGE_MESSAGE =
  "Trop de tentatives. Validez le contrôle anti-robot ci-dessous, puis réessayez.";

/**
 * Challenge adaptatif pour les formulaires d'authentification.
 *
 * Le widget n'existe pas tant que le serveur ne l'a pas demandé : en usage
 * normal l'utilisateur ne voit jamais rien.
 *
 * ```tsx
 * const captcha = useCaptchaChallenge();
 * ...
 * try { await login(email, password, captcha.token); }
 * catch (err) { if (!captcha.handleError(err.message)) setError(err.message); }
 * ...
 * {captcha.node}
 * ```
 */
export function useCaptchaChallenge() {
  const [required, setRequired] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const ref = useRef<TurnstileHandle>(null);

  /**
   * À appeler dans le `catch` d'une mutation d'auth. Renvoie `true` quand
   * l'erreur était une demande de challenge : l'appelant n'affiche alors pas le
   * message brut (`CAPTCHA_REQUIRED` ne veut rien dire pour un utilisateur).
   */
  const handleError = useCallback((message: string | undefined | null): boolean => {
    // Dans TOUS les cas le token vient d'être consommé par la tentative : sans
    // ce reset, l'essai suivant rejouerait un jeton déjà utilisé et échouerait
    // en boucle.
    ref.current?.reset();
    if (!message || !message.includes(CAPTCHA_REQUIRED)) return false;
    setRequired(true);
    return true;
  }, []);

  const node = required ? (
    // `always` et non le défaut `interaction-only` : CAPTCHA_CHALLENGE_MESSAGE
    // vient d'annoncer « validez le contrôle anti-robot ci-dessous ». Un widget
    // silencieux ferait pointer ce message vers une zone vide.
    <Turnstile ref={ref} onToken={setToken} className="flex justify-center" appearance="always" />
  ) : null;

  return { required, token: token ?? undefined, node, handleError, message: CAPTCHA_CHALLENGE_MESSAGE };
}
